import { GameConfig } from './GameConfig';

export interface RoomInfo {
  code: string;
  peerId: string;
  hostName: string;
}

/**
 * Client-side service for the Platformia matchmaking server.
 * Manages room registration and lookup via REST API.
 */
export class MatchmakingService {

  /**
   * Register a new room on the server (called by host).
   */
  static async registerRoom(code: string, peerId: string, hostName: string): Promise<boolean> {
    try {
      const res = await fetch(`${GameConfig.serverUrl}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, peerId, hostName }),
      });
      if (!res.ok) {
        console.warn('[Matchmaking] Failed to register room:', res.status);
        return false;
      }
      console.log('[Matchmaking] Room registered:', code);
      return true;
    } catch (err) {
      console.warn('[Matchmaking] Server unreachable, room not registered:', err);
      return false;
    }
  }

  /**
   * Look up a room code to get the PeerJS peer ID (called by joiner).
   * Returns the peerId if found, or null if not found / server down.
   */
  static async lookupRoom(code: string): Promise<string | null> {
    try {
      const res = await fetch(`${GameConfig.serverUrl}/rooms/${encodeURIComponent(code)}`);
      if (!res.ok) {
        console.warn('[Matchmaking] Room not found:', code);
        return null;
      }
      const data = await res.json();
      console.log('[Matchmaking] Room found:', code, '→', data.peerId);
      return data.peerId;
    } catch (err) {
      console.warn('[Matchmaking] Server unreachable for lookup:', err);
      return null;
    }
  }

  /**
   * List all available rooms on the server.
   */
  static async listRooms(): Promise<RoomInfo[]> {
    try {
      const res = await fetch(`${GameConfig.serverUrl}/rooms`);
      if (!res.ok) {
        console.warn('[Matchmaking] Failed to list rooms:', res.status);
        return [];
      }
      const data = await res.json();
      return data.rooms || [];
    } catch (err) {
      console.warn('[Matchmaking] Server unreachable for room list:', err);
      return [];
    }
  }

  /**
   * Remove a room from the server (called on disconnect/cleanup).
   */
  static async unregisterRoom(code: string): Promise<void> {
    try {
      await fetch(`${GameConfig.serverUrl}/rooms/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      });
      console.log('[Matchmaking] Room unregistered:', code);
    } catch (err) {
      // Best-effort cleanup, ignore errors
      console.warn('[Matchmaking] Failed to unregister room:', err);
    }
  }
}
